import React, { useState } from "react";
import { Form, Input, Button, Typography, Card, Tag, Select, Divider } from "antd";
import { SendOutlined, SaveOutlined, BulbOutlined, HeartOutlined } from "@ant-design/icons";
import { onMessageSend } from "../../../hooks/onMessageSend";
import { useAppSelector } from "../../../hooks/redux-hooks";
import {
  RegExpMatcher,
  englishDataset,
  englishRecommendedTransformers,
} from "obscenity";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const matcher = new RegExpMatcher({
  ...englishDataset.build(),
  ...englishRecommendedTransformers,
});

const MessageComposer = ({ recipientName }) => {
  const currentChat = useAppSelector((state) => state.currentChat);
  const [message, setMessage] = useState({ text: "", subject: "" });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const hasProfanity = matcher.hasMatch(message.text);
  const isTooShort = message.text.trim().length < 10;
  const isTooLong = message.text.length > 1000;
  const isEmpty = message.text.trim() === "";
  const isDisabled = isEmpty || hasProfanity || isTooShort || isTooLong;

  // Message templates and conversation starters
  const conversationStarters = [
    "I noticed we both value family traditions. I'd love to hear about your family celebrations.",
    "Your profile mentions your love for travel. What's been your most meaningful journey?",
    "I'm impressed by your educational background. What inspired you to pursue your field?",
    "We seem to have similar life goals. I'd like to know more about your vision for the future.",
    "Your interests in arts/culture caught my attention. Do you have any favorite cultural activities?"
  ];

  const messageTemplates = [
    {
      title: "Thoughtful Introduction",
      content: `Dear ${recipientName},\n\nI hope this message finds you well. I came across your profile and was drawn to [specific detail from their profile]. I believe we might have some meaningful things to discuss.\n\nI would love to learn more about your thoughts on [shared interest/value].\n\nLooking forward to hearing from you.\n\nWarm regards`
    },
    {
      title: "Follow-up Message",
      content: `Hi ${recipientName},\n\nThank you for your previous message. I found your perspective on [topic] very interesting.\n\nI wanted to share my thoughts on [related topic] and would love to continue our conversation.\n\nBest regards`
    }
  ];

  const handleSend = () => {
    if (!isDisabled) {
      onMessageSend(message, currentChat.userID, currentChat.chatID);
      setMessage({ text: "", subject: "" });
      setSelectedTemplate(null);
    }
  };

  const applyTemplate = (template) => {
    setMessage({ ...message, text: template.content });
    setSelectedTemplate(template.title);
    setShowSuggestions(false);
  };

  const applyStarter = (starter) => {
    setMessage({ ...message, text: starter });
    setShowSuggestions(false);
  };

  const getCharacterCountColor = () => {
    if (isTooShort) return '#ff4d4f';
    if (message.text.length > 800) return '#faad14';
    return '#52c41a';
  };

  return (
    <Card
      style={{
        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 255, 0.95) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
      }}
      bodyStyle={{ padding: '20px' }}
    >
      {/* Composer Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <Title level={5} style={{ margin: 0, color: '#2c3e50' }}>
          Compose Message to {recipientName}
        </Title>
        <Button
          type="text"
          icon={<BulbOutlined />}
          onClick={() => setShowSuggestions(!showSuggestions)}
          style={{ color: '#667eea' }}
        >
          Get Ideas
        </Button>
      </div>

      {/* Conversation Starters & Templates */}
      {showSuggestions && (
        <Card 
          size="small"
          style={{ 
            marginBottom: '16px',
            background: 'rgba(102, 126, 234, 0.05)',
            border: '1px solid rgba(102, 126, 234, 0.1)'
          }}
        >
          <div style={{ marginBottom: '12px' }}>
            <Text strong style={{ color: '#667eea' }}>Conversation Starters:</Text>
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {conversationStarters.slice(0, 3).map((starter, index) => (
                <Button
                  key={index}
                  type="text"
                  size="small"
                  onClick={() => applyStarter(starter)}
                  style={{
                    textAlign: 'left',
                    height: 'auto',
                    padding: '8px 12px',
                    whiteSpace: 'normal',
                    fontSize: '13px',
                    color: '#64748b'
                  }}
                >
                  "{starter.substring(0, 80)}..."
                </Button>
              ))}
            </div>
          </div>
          
          <Divider style={{ margin: '12px 0' }} />
          
          <div>
            <Text strong style={{ color: '#667eea' }}>Message Templates:</Text>
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
              {messageTemplates.map((template, index) => (
                <Button
                  key={index}
                  size="small"
                  onClick={() => applyTemplate(template)}
                  style={{ borderRadius: '8px' }}
                >
                  {template.title}
                </Button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Subject Line (Optional) */}
      <Form.Item
        label={<Text style={{ color: '#64748b', fontWeight: '500' }}>Subject (Optional)</Text>}
        style={{ marginBottom: '16px' }}
      >
        <Input
          value={message.subject}
          placeholder="What's this message about?"
          onChange={(e) => setMessage({ ...message, subject: e.target.value })}
          style={{
            borderRadius: '8px',
            background: 'rgba(255, 255, 255, 0.9)'
          }}
        />
      </Form.Item>

      {/* Message Composition */}
      <Form.Item
        validateStatus={hasProfanity ? "error" : isTooShort ? "warning" : isTooLong ? "error" : "success"}
        help={
          hasProfanity ? (
            <Text type="danger">Message contains inappropriate content</Text>
          ) : isTooShort && !isEmpty ? (
            <Text type="warning">Consider writing a more thoughtful message (minimum 10 characters)</Text>
          ) : isTooLong ? (
            <Text type="danger">Message too long (maximum 1000 characters)</Text>
          ) : null
        }
      >
        <TextArea
          value={message.text}
          placeholder="Take your time to write a thoughtful message. Share something meaningful about yourself or ask about their interests..."
          onChange={(e) => setMessage({ ...message, text: e.target.value })}
          autoSize={{ minRows: 4, maxRows: 8 }}
          style={{
            borderRadius: '12px',
            fontSize: '15px',
            lineHeight: '1.6',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}
        />
      </Form.Item>

      {/* Character Count & Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Text style={{ 
            fontSize: '13px',
            color: getCharacterCountColor(),
            fontWeight: '500'
          }}>
            {message.text.length}/1000 characters
          </Text>
          {selectedTemplate && (
            <Tag color="blue" size="small">
              Using: {selectedTemplate}
            </Tag>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            icon={<SaveOutlined />}
            style={{ borderRadius: '8px' }}
          >
            Save Draft
          </Button>
          <Button
            type="primary"
            disabled={isDisabled}
            onClick={handleSend}
            style={{
              background: isDisabled 
                ? undefined
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderColor: isDisabled ? undefined : '#667eea',
              borderRadius: '8px',
              fontWeight: '600',
              height: '40px',
              padding: '0 24px',
              boxShadow: isDisabled 
                ? undefined
                : '0 4px 12px rgba(102, 126, 234, 0.4)'
            }}
            icon={<SendOutlined />}
          >
            Send Message
          </Button>
        </div>
      </div>

      {/* Helpful Tips */}
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        background: 'rgba(102, 126, 234, 0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(102, 126, 234, 0.1)'
      }}>
        <Text style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
          💡 Tip: Meaningful conversations start with genuine interest. Ask thoughtful questions and share something personal about yourself.
        </Text>
      </div>
    </Card>
  );
};

export default MessageComposer;